import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { sortBy } from 'es-toolkit'
import { DataTableSortStatus } from 'mantine-datatable'
import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import type { CoinItemInGetList, CoinItemInPostList } from '@/services/coin/coins'
import { coinsQueryOptions, useCoinsByIds } from '@/services/coin/coins'
import type { TokenizedStockItem } from '@/services/coin/tokenizedStocks'
import { tokenizedStocksQueryOptions } from '@/services/coin/tokenizedStocks'
import type { CoinId, PopularCoin, TopMoverItem } from '@/types/coin'

import {
  COINS_OG_PAGE_SIZE,
  COINS_PAGE_SIZE,
  CoinTab,
  TOKENIZED_STOCKS_PAGE_SIZE,
} from '../../configs'
import { useCoinsTableStore } from '../../context'
import type { TableRecord } from '../columns/configs'

//Build query options with pageSize override
const buildQueryOptions = (
  tab: any,
  page: number,
  pageSize: number,
  sort?: DataTableSortStatus<TableRecord>,
  sortInRequest?: boolean,
  fetchBlockSize?: number,
) => {
  if ([CoinTab.AllCoins, CoinTab.AllAssets].includes(tab.value)) {
    // fetchBlockSize 模式：接口按大块（如 100 条）拉，page 是块页码（展示页
    // 每 fetchBlockSize/pageSize 页对应一个接口块）。否则 page 就是展示页码。
    const ratio = fetchBlockSize ? fetchBlockSize / pageSize : 1
    const blockPage = Math.ceil(page / ratio)
    return coinsQueryOptions({
      page: blockPage,
      limit: fetchBlockSize ?? pageSize,
      ...(sortInRequest && sort
        ? { sort: sort.sortKey, direction: sort.direction === 'asc' ? 'ASC' : 'DESC' }
        : {}),
    })
  }
  return tab.getQueryOptions ? tab.getQueryOptions(page) : tab.queryOptions
}

// Get the ideal table state to avoid hiding the table when the query is suspending
function useIdealTableState(
  pageSize: number,
  sort?: DataTableSortStatus<TableRecord>,
  sortInRequest?: boolean,
  fetchBlockSize?: number,
) {
  const { currentCoinTab, currentPage } = useCoinsTableStore(
    useShallow(({ currentCoinTab, currentPage }) => ({
      currentCoinTab,
      currentPage,
    })),
  )

  const currentTabQueryOptions = buildQueryOptions(
    currentCoinTab,
    currentPage,
    pageSize,
    sort,
    sortInRequest,
    fetchBlockSize,
  )

  // Defer the values re-render to avoid triggering suspense fallback
  const deferredCoinTab = useDeferredValue(currentCoinTab)
  const deferredPage = useDeferredValue(currentPage)

  const deferredTabQueryOptions = buildQueryOptions(
    deferredCoinTab,
    deferredPage,
    pageSize,
    sort,
    sortInRequest,
    fetchBlockSize,
  )

  const queryClient = useQueryClient()
  const queryState = queryClient.getQueryState(currentTabQueryOptions.queryKey)
  const isPending = !queryState?.data

  if (isPending) {
    return {
      coinTab: deferredCoinTab,
      tabQueryOptions: deferredTabQueryOptions,
      currentPage: deferredPage,
      isPending,
    }
  }

  return {
    coinTab: currentCoinTab,
    tabQueryOptions: currentTabQueryOptions,
    currentPage,
    isPending,
  }
}

export default function useTableData({
  isOGPrice,
  initialSortStatus,
  pageSize: pageSizeProp,
  sortInRequest,
  fetchBlockSize,
}: {
  isOGPrice?: boolean
  initialSortStatus?: DataTableSortStatus<TableRecord>
  /** Per-page override (e.g. the HomepageV2 crypto tab's 20-row page). */
  pageSize?: number
  sortInRequest?: boolean
  /**
   * Fetch size override (HomepageV2 crypto): the API is paged in blocks of
   * `fetchBlockSize` records (keeps the API page count under the backend's
   * deep-paging cap, unlike pageSize=20), while the UI paginates in `pageSize`
   * rows. `currentPage` stays the display page; this hook maps it to the block
   * and slices the display window. Omit for the legacy /price list.
   */
  fetchBlockSize?: number
}) {
  const pageSize = pageSizeProp ?? (isOGPrice ? COINS_OG_PAGE_SIZE : COINS_PAGE_SIZE)

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<TableRecord> | undefined>(
    initialSortStatus,
  )

  const { coinTab, tabQueryOptions, currentPage, isPending } = useIdealTableState(
    pageSize,
    sortStatus,
    sortInRequest,
    fetchBlockSize,
  )

  const { data } = useSuspenseQuery(tabQueryOptions)

  const { data: rawTokenizedStocks } = useQuery({
    ...tokenizedStocksQueryOptions(),
    enabled: [CoinTab.AllCoins, CoinTab.AllAssets].includes(coinTab.value),
  })

  const tokenizedStockSlugSet = useMemo(
    () => new Set((rawTokenizedStocks ?? []).map(s => s.slug)),
    [rawTokenizedStocks],
  )

  let totalRecords = 0
  let coins:
    | CoinItemInGetList[]
    | TopMoverItem[]
    | PopularCoin[]
    | CoinItemInPostList[]
    | TokenizedStockItem[] = []

  // Handle different response structures based on tab type
  if ([CoinTab.AllCoins, CoinTab.AllAssets, CoinTab.New].includes(coinTab.value)) {
    // @ts-expect-error Complex mixed query options that useSuspenseQuery can't handle
    coins = data.data
    // @ts-expect-error Complex mixed query options that useSuspenseQuery can't handle
    totalRecords = data.total

    // Only show 1 page of new coins (your current behavior)
    if (coinTab.value === CoinTab.New) {
      totalRecords = coins.length
    }
  } else if ([CoinTab.TopGainers, CoinTab.TopLosers].includes(coinTab.value)) {
    coins = data as unknown as TopMoverItem[]
    totalRecords = coins.length
  } else if ([CoinTab.MostPopular, CoinTab.Favorites].includes(coinTab.value)) {
    coins = data as unknown as PopularCoin[]
    totalRecords = coins.length
  } else if (coinTab.value === CoinTab.TokenizedStocks) {
    coins = data as unknown as TokenizedStockItem[]
    totalRecords = coins.length
  }

  // Tabs other than AllCoins and New need another query to get batch coins by ids
  const isTabNeedsAnotherQuery = [
    CoinTab.TopGainers,
    CoinTab.TopLosers,
    CoinTab.MostPopular,
    CoinTab.Favorites,
    CoinTab.TokenizedStocks,
  ].includes(coinTab.value)

  // Apply client paging for list-tabs BEFORE mapping ids
  let pagedCoins = coins
  if (isOGPrice && isTabNeedsAnotherQuery) {
    const start = (currentPage - 1) * pageSize
    const end = start + pageSize
    pagedCoins = (coins as any[]).slice(start, end)
  } else if (coinTab.value === CoinTab.TokenizedStocks) {
    const start = (currentPage - 1) * TOKENIZED_STOCKS_PAGE_SIZE
    const end = start + TOKENIZED_STOCKS_PAGE_SIZE
    pagedCoins = (coins as any[]).slice(start, end)
  }

  let coinIds: CoinId[] = []
  if (isTabNeedsAnotherQuery) {
    coinIds = (pagedCoins as any[]).map((coin: any) => coin[coinTab.coinIdKey] as CoinId)
  }

  const { data: batchCoins } = useCoinsByIds(coinIds)
  if (isTabNeedsAnotherQuery) {
    coins = batchCoins
  }

  const filteredCoins = useMemo(() => {
    if (coinTab.value === CoinTab.AllCoins && tokenizedStockSlugSet.size > 0) {
      return (coins as CoinItemInGetList[]).filter(coin => !tokenizedStockSlugSet.has(coin.slug))
    }
    return coins
  }, [coinTab.value, tokenizedStockSlugSet, coins])

  // fetchBlockSize 模式：接口块（fetchBlockSize 条）切成当前展示页（pageSize
  // 条）的窗口。展示页 currentPage 落在第 ceil(currentPage/ratio) 块内，
  // 块内偏移 = (currentPage-1) % ratio。
  const finalCoins = useMemo(() => {
    if (!fetchBlockSize) {
      return filteredCoins as CoinItemInGetList[] | CoinItemInPostList[]
    }
    const ratio = fetchBlockSize / pageSize
    const offsetInBlock = ((currentPage - 1) % ratio) * pageSize
    return (filteredCoins as CoinItemInGetList[]).slice(offsetInBlock, offsetInBlock + pageSize) as
      | CoinItemInGetList[]
      | CoinItemInPostList[]
  }, [fetchBlockSize, filteredCoins, currentPage, pageSize])

  const [records, setRecords] = useState(finalCoins)

  function handleSortStatusChange(newSortStatus: DataTableSortStatus<TableRecord>) {
    setSortStatus({
      ...newSortStatus,
      direction: newSortStatus.sortKey !== sortStatus?.sortKey ? 'desc' : newSortStatus.direction,
    })
  }

  useEffect(() => {
    if (!sortStatus) {
      setRecords(finalCoins)
    } else {
      // @ts-expect-error Hybrid coins type
      const sortedCoins = sortBy(finalCoins, [sortStatus.sortKey])
      setRecords(sortStatus.direction === 'desc' ? sortedCoins.reverse() : sortedCoins)
    }
  }, [sortStatus, finalCoins])

  return {
    isPending,
    records,
    totalRecords, // keep full total so pagination count is correct
    sortStatus,
    handleSortStatusChange,
    tokenizedStockSlugSet,
  }
}
